import {
  chaveErroInspecao,
  codigosPermitidos,
  mensagemCentroAceita,
  tagCentroAdicional,
} from "../hooks/nova-obra.tipos";
import { createElement, useEffect, useMemo, useRef, useState } from "react";
import type { OverlayPanel } from "primereact/overlaypanel";
import type { Menu as PrimeMenu } from "primereact/menu";
import {
  useObraCentrosCusto,
  useObraEmbarcacaoInformacoes,
  useObraEmbarcacoes,
  useObraPeriodos,
  useObraTiposObra,
} from "@/modules/obras";
import {
  adicionarCentro,
  adicionarTipo,
  alterarDominante,
  atualizarTipo,
  centroPrincipalPadrao,
  centrosAdicionaveis,
  centrosPrincipais,
  criarTipoItem,
  motivoTipoNaoRestauravel,
  PRIORIDADE_DOMINANTE,
  naturezaCentroCusto,
  novoUidTipos,
  normalizarDominante,
  readTiposObra,
  removerCentro,
  removerTipo,
  restaurarCentro,
  restaurarTipo,
  rotuloCentroCusto,
  rotuloTipo,
  sincronizarManutencaoCorretiva,
  tiposDisponiveis,
  tipoDominante as tipoDominanteDaObra,
  trocarCentroPrincipal,
  trocarCentroPrincipalComCorretiva,
} from "../hooks/nova-obra.tipos";
import { calcularCronograma, intervaloPeriodoObra, readDuracao } from "../hooks/nova-obra.duracao";
import { useToast } from "@/ds";
import { periodoDaObraNoWizard } from "../hooks/nova-obra.equipe";
import { useNovaObra } from "../hooks/nova-obra.context";
import type { CentroCustoOpcao, TipoObraOpcao } from "@/modules/obras";
import type {
  CentroAdicional,
  DadosTiposObra,
  NaturezaCentroCusto,
  TipoObraItem,
} from "../hooks/nova-obra.tipos";
import { CARREGANDO, DOMINANTE_LABELS, TIPOS_LABELS, TIPOS_TOAST } from "../nova-obra.constants";
import type { SelectOption } from "../nova-obra.constants";
import { DesfazerToastDetalhe } from "../components/desfazer-toast-detalhe";
import { SEM_VALOR } from "./etapa-tipos-obra.constants";
import type { RemocaoToast } from "./types-etapa-tipos-obra";

export function useEtapaTiposObra() {
  const { state, errosVisiveis, salvarEtapa } = useNovaObra();
  const dados = useMemo(() => readTiposObra(state.etapas.tiposObra), [state.etapas.tiposObra]);
  const dadosRef = useRef(dados);
  dadosRef.current = dados;
  const toast = useToast();
  const menuTipos = useRef<PrimeMenu>(null);
  const painelCentros = useRef<OverlayPanel>(null);
  /** Centro cujo "Adicionar tipo de obra" abriu o menu (`null` = CC principal). */
  const centroDoMenu = useRef<string | null>(null);
  const [menuItens, setMenuItens] = useState<{ label: string; command: () => void }[]>([]);
  const [abertos, setAbertos] = useState<ReadonlySet<string>>(new Set());
  const [busca, setBusca] = useState("");
  const [verLimites, setVerLimites] = useState(false);
  /** Tipo escolhido na opção "Tornar esse tipo de obra como dominante?" (modal aberto; nada efetivado). */
  const [novoDominante, setNovoDominante] = useState<TipoObraItem | null>(null);
  const { embarcacaoId, manutencaoCorretiva } = state.obra;
  const catalogo = useObraTiposObra();
  const centros = useObraCentrosCusto(embarcacaoId);
  const embarcacoes = useObraEmbarcacoes();
  const informacoes = useObraEmbarcacaoInformacoes(embarcacaoId);
  const periodos = useObraPeriodos();
  const duracao = useMemo(() => readDuracao(state.etapas.duracao), [state.etapas.duracao]);
  const cronograma = useMemo(() => calcularCronograma(duracao), [duracao]);
  const periodoObra = useMemo(
    () => intervaloPeriodoObra(duracao, cronograma, periodos.data),
    [duracao, cronograma, periodos.data],
  );
  const periodoWizard = useMemo(
    () => periodoDaObraNoWizard(state.etapas.duracao),
    [state.etapas.duracao],
  ); // Atualiza a ref na hora: duas alterações no mesmo ciclo (efeitos, cliques) partem do mais recente.
  const salvar = (proximo: DadosTiposObra) => {
    const normalizado = normalizarDominante(proximo);
    dadosRef.current = normalizado;
    salvarEtapa("tiposObra", normalizado);
  }; // Lista de CCs carregada: o principal vira obrigatório e, sem escolha válida, vem o de sufixo X (RN03).
  useEffect(() => {
    if (centros.status !== "success") return;

    const atual = dadosRef.current;
    const exige = centros.data.length > 0;
    const valido = centros.data.some((centro) => centro.id === atual.centroCustoPrincipalId);

    if (exige === atual.exigeCentroPrincipal && (valido || !exige)) return;

    const padrao = valido ? null : centroPrincipalPadrao(centros.data);
    const base = { ...atual, exigeCentroPrincipal: exige };
    salvar(
      !valido && exige
        ? trocarCentroPrincipal(base, padrao)
        : { ...base, centroCustoPrincipalId: valido ? atual.centroCustoPrincipalId : null },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centros.status, centros.data]); // RN11: Manutenção Corretiva (etapa 1) reflete o tipo 301 no CC principal.
  useEffect(() => {
    if (catalogo.status !== "success") return;

    const atual = dadosRef.current;
    const proximo = sincronizarManutencaoCorretiva(atual, manutencaoCorretiva, catalogo.data);
    if (proximo !== atual) salvar(proximo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogo.status, catalogo.data, manutencaoCorretiva]); // Tipo recém-adicionado abre expandido (Figma estado 10).
  const alternar = (uid: string) =>
    setAbertos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(uid)) proximo.delete(uid);
      else proximo.add(uid);
      return proximo;
    });
  const mostrarToast = ({ summary, detail, onDesfazer }: RemocaoToast) =>
    toast.show({
      severity: "secondary",
      summary,
      life: 8000,
      detail: createElement(DesfazerToastDetalhe, {
        detalhe: detail,
        rotulo: TIPOS_TOAST.desfazer,
        onDesfazer: () => {
          const avisou = onDesfazer();
          if (!avisou) toast.clear();
        },
      }),
    });
  const naturezaPrincipalAtual = (): NaturezaCentroCusto => {
    const centro = centros.data.find((item) => item.id === dadosRef.current.centroCustoPrincipalId);
    return centro ? naturezaCentroCusto(centro) : "obra";
  };
  const avisar = (summary: string, detail: string) =>
    // `replace` troca o toast de Desfazer pelo aviso; `clear()` + `show()` deixava o aviso sem aparecer.
    toast.replace({ severity: "warn", summary, detail, life: 8000 });
  const atualizar = (uid: string, campos: Partial<TipoObraItem>) =>
    salvar(atualizarTipo(dadosRef.current, uid, campos));
  const removerTipoDoCentro = (
    tipo: TipoObraItem,
    centroUid: string | null,
    tiposDoCentro: readonly TipoObraItem[],
  ) => {
    const indice = tiposDoCentro.findIndex((item) => item.uid === tipo.uid);
    salvar(removerTipo(dadosRef.current, tipo.uid));
    mostrarToast({
      summary: TIPOS_TOAST.tipoSummary,
      detail: TIPOS_TOAST.tipoDetail(rotuloTipo(tipo)),
      onDesfazer: () => {
        // RN07/RN08: a obra pode ter mudado desde a remoção; só volta se o tipo ainda for válido.
        const atual = dadosRef.current;
        const natureza = centroUid
          ? naturezaCentroCusto(
              atual.centrosAdicionais.find((item) => item.uid === centroUid)?.centro ?? {
                centroCusto: "",
                subgrupo: "",
              },
            )
          : naturezaPrincipalAtual();
        const motivo = motivoTipoNaoRestauravel(atual, tipo, natureza, centroUid, catalogo.data);
        if (motivo) {
          avisar(
            TIPOS_TOAST.naoRestauradoSummary,
            TIPOS_TOAST.naoRestaurado(rotuloTipo(tipo), motivo),
          );
          return true;
        }
        salvar(restaurarTipo(atual, centroUid, indice, tipo));
        return false;
      },
    });
  };
  const removerCentroAdicional = (adicional: CentroAdicional) => {
    const indice = dadosRef.current.centrosAdicionais.findIndex(
      (item) => item.uid === adicional.uid,
    );
    salvar(removerCentro(dadosRef.current, adicional.uid));
    mostrarToast({
      summary: TIPOS_TOAST.centroSummary,
      detail: TIPOS_TOAST.centroDetail(rotuloCentroCusto(adicional.centro)),
      onDesfazer: () => {
        const atual = dadosRef.current;
        const natureza = naturezaCentroCusto(adicional.centro);
        // Tipos que já não cabem (repetidos no CC/excludentes) ficam de fora; o aviso diz quais.
        const mantidos: TipoObraItem[] = [];
        const barrados: TipoObraItem[] = [];
        for (const tipo of adicional.tipos) {
          const simulado = {
            ...atual,
            centrosAdicionais: [...atual.centrosAdicionais, { ...adicional, tipos: mantidos }],
          };
          (motivoTipoNaoRestauravel(simulado, tipo, natureza, adicional.uid, catalogo.data)
            ? barrados
            : mantidos
          ).push(tipo);
        }
        salvar(restaurarCentro(atual, indice, { ...adicional, tipos: mantidos }));
        if (barrados.length > 0) {
          avisar(
            TIPOS_TOAST.naoRestauradoSummary,
            TIPOS_TOAST.centroSemTiposRestaurados(barrados.map(rotuloTipo).join(", ")),
          );
          return true;
        }
        return false;
      },
    });
  };
  const abrirMenuTipos = (
    event: React.SyntheticEvent,
    centroUid: string | null,
    natureza: NaturezaCentroCusto,
  ) => {
    centroDoMenu.current = centroUid;
    const disponiveis = tiposDisponiveis(catalogo.data, dadosRef.current, natureza, centroUid);
    setMenuItens(
      disponiveis.map((tipo) => ({
        label: rotuloTipo(tipo),
        command: () => incluirTipo(tipo, centroUid),
      })),
    );
    menuTipos.current?.toggle(event);
  };
  const incluirTipo = (tipo: TipoObraOpcao, centroUid: string | null) => {
    const item = criarTipoItem(tipo);
    salvar(adicionarTipo(dadosRef.current, centroUid, item));
    setAbertos((atual) => new Set(atual).add(item.uid));
  };
  const escolherPrincipal = (id: string | null) => {
    const centro = centros.data.find((item) => item.id === id) ?? null;
    const proximo = trocarCentroPrincipalComCorretiva(
      dadosRef.current,
      centro,
      manutencaoCorretiva,
      catalogo.data,
    );
    if (!proximo) {
      // RN11: a corretiva exige o 301; o CC que não o aceita não vira principal.
      avisar(
        TIPOS_TOAST.centroSemCorretivaSummary,
        TIPOS_TOAST.centroSemCorretiva(centro?.centroCusto ?? ""),
      );
      return;
    }
    salvar(proximo);
  };
  const incluirCentro = (centro: CentroCustoOpcao) => {
    painelCentros.current?.hide();
    limparBusca();
    salvar(
      adicionarCentro(dadosRef.current, {
        uid: novoUidTipos("centro"),
        centro: {
          id: centro.id,
          centroCusto: centro.centroCusto,
          descricao: centro.descricao,
          subgrupo: centro.subgrupo,
        },
        tipos: [],
      }),
    );
  };
  const principal = centros.data.find((centro) => centro.id === dados.centroCustoPrincipalId);
  const naturezaPrincipal: NaturezaCentroCusto = principal
    ? naturezaCentroCusto(principal)
    : "obra";
  const opcoesPrincipal = useMemo<SelectOption[]>(
    () =>
      centrosPrincipais(centros.data, dados).map((centro) => ({
        value: centro.id,
        label: rotuloCentroCusto(centro),
      })),
    [centros.data, dados],
  );
  const disponiveisPrincipal = tiposDisponiveis(catalogo.data, dados, naturezaPrincipal, null);
  const clientes = useMemo<SelectOption[]>(() => {
    const nomes = new Set<string>();
    if (informacoes.data?.clienteAtual) nomes.add(informacoes.data.clienteAtual);
    return [...nomes].map((nome) => ({ value: nome, label: nome }));
  }, [informacoes.data]); // O cliente já escolhido continua visível mesmo se a lista mudar.
  const clientesDoTipo = (tipo: TipoObraItem) =>
    tipo.cliente && !clientes.some((opcao) => opcao.value === tipo.cliente)
      ? [...clientes, { value: tipo.cliente, label: tipo.cliente }]
      : clientes; // Ao voltar para a etapa as consultas recarregam: mostra "Carregando…" em vez de "—".
  const embarcacaoNome =
    embarcacoes.data.find((item) => item.id === embarcacaoId)?.nome ??
    (embarcacoes.status === "loading" ? CARREGANDO : SEM_VALOR);
  const adicionaveis = centrosAdicionaveis(centros.data, dados);
  const grupos = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const filtrados = adicionaveis.filter(
      (centro) => !termo || rotuloCentroCusto(centro).toLowerCase().includes(termo),
    );
    return [
      {
        titulo: TIPOS_LABELS.grupoManutencaoOperacional,
        itens: filtrados
          .filter((centro) => naturezaCentroCusto(centro) === "manutencao-operacional")
          .map((centro) => ({ centro, rotulo: rotuloCentroCusto(centro) })),
      },
      {
        titulo: TIPOS_LABELS.grupoSubsea,
        itens: filtrados
          .filter((centro) => naturezaCentroCusto(centro) === "subsea")
          .map((centro) => ({ centro, rotulo: rotuloCentroCusto(centro) })),
      },
    ].filter((grupo) => grupo.itens.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centros.data, dados, busca]);
  const tipoDominante = tipoDominanteDaObra(dados) ?? undefined;
  const uidDominante = tipoDominante?.uid ?? null; // Nomenclatura vigente (`GET tipos-obra`); a ordem vem da prioridade por código (RN016).
  const prioridadeTexto = useMemo(
    () =>
      [
        DOMINANTE_LABELS.tooltipIntro,
        "",
        ...PRIORIDADE_DOMINANTE.map((codigo, indice) => {
          const nome = catalogo.data.find((tipo) => tipo.codigo === codigo)?.nome;
          return `${indice + 1}. ${nome ? `${nome} (${codigo})` : codigo}`;
        }),
      ].join("\n"),
    [catalogo.data],
  );
  const alterarBusca = (valor: string) => setBusca(valor);
  const limparBusca = () => setBusca("");
  const abrirLimites = () => setVerLimites(true);
  const fecharLimites = () => setVerLimites(false);
  const escolherNovoDominante = (tipo: TipoObraItem) => setNovoDominante(tipo);
  const cancelarDominante = () => setNovoDominante(null);
  const confirmarDominante = (justificativa: string) => {
    if (novoDominante) {
      salvar(alterarDominante(dadosRef.current, novoDominante.uid, justificativa));
    }
    cancelarDominante();
  };
  const todosAdicionados = centros.status === "success" && adicionaveis.length === 0;
  const erroPrincipal = errosVisiveis.centroCustoPrincipalId;
  const semCentroCadastrado = centros.status === "success" && centros.data.length === 0;
  const mostraHintX = principal ? naturezaCentroCusto(principal) === "obra" : false;

  return {
    errosVisiveis,
    dados,
    menuTipos,
    painelCentros,
    menuItens,
    abertos,
    busca,
    alterarBusca,
    limparBusca,
    verLimites,
    abrirLimites,
    fecharLimites,
    novoDominante,
    escolherNovoDominante,
    cancelarDominante,
    embarcacaoId,
    catalogo,
    centros,
    informacoes,
    periodos,
    duracao,
    cronograma,
    periodoObra,
    periodoWizard,
    alternar,
    atualizar,
    removerTipoDoCentro,
    removerCentroAdicional,
    abrirMenuTipos,
    escolherPrincipal,
    incluirCentro,
    naturezaPrincipal,
    opcoesPrincipal,
    disponiveisPrincipal,
    clientesDoTipo,
    embarcacaoNome,
    adicionaveis,
    grupos,
    uidDominante,
    tipoDominante,
    prioridadeTexto,
    confirmarDominante,
    todosAdicionados,
    erroPrincipal,
    semCentroCadastrado,
    mostraHintX,
    errosInspecao: Object.fromEntries(
      [...dados.tipos, ...dados.centrosAdicionais.flatMap((adicional) => adicional.tipos)].map(
        (tipo) => [tipo.uid, errosVisiveis[chaveErroInspecao(tipo.uid)]],
      ),
    ),
    centrosAdicionais: dados.centrosAdicionais.map((adicional) => {
      const natureza = naturezaCentroCusto(adicional.centro);
      const [codigoUnico] = codigosPermitidos(natureza) ?? [];

      return {
        adicional,
        testId: `centro-adicional-${adicional.centro.centroCusto}`,
        natureza,
        tag: tagCentroAdicional(adicional.centro),
        rotulo: rotuloCentroCusto(adicional.centro),
        aceita: mensagemCentroAceita(natureza, catalogo.data),
        semTiposDisponiveis:
          tiposDisponiveis(catalogo.data, dados, natureza, adicional.uid).length === 0,
        codigoUnico,
        nomeUnico: catalogo.data.find((tipo) => tipo.codigo === codigoUnico)?.nome ?? "",
      };
    }),
  };
}
